import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, ArrowUpCircle, ArrowDownCircle, History, User, Settings, 
  Shield, LogOut, Bell, ChevronRight, TrendingUp, Wallet, Users,
  CheckCircle, Star, Gift, Zap, BarChart3, MessageSquare
} from "lucide-react";

export default function BybitMain() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [balances, setBalances] = useState({});
  const [showBrowser, setShowBrowser] = useState(true);

  // Set body background
  useEffect(() => {
    // Force full height and background on all ancestor elements
    const rootEl = document.getElementById('root');
    document.documentElement.classList.add('bybit-theme');
    document.body.classList.add('bybit-theme');
    document.body.style.cssText = 'margin: 0; padding: 0; background-color: #1a1a2e; min-height: 100vh;';
    document.documentElement.style.cssText = 'background-color: #1a1a2e; min-height: 100vh;';
    if (rootEl) {
      rootEl.classList.add('bybit-theme');
      rootEl.style.cssText = 'min-height: 100vh; background-color: #1a1a2e;';
    }
    return () => {
      document.documentElement.classList.remove('bybit-theme');
      document.body.classList.remove('bybit-theme');
      document.body.style.cssText = '';
      document.documentElement.style.cssText = '';
      if (rootEl) {
        rootEl.classList.remove('bybit-theme');
        rootEl.style.cssText = '';
      }
    };
  }, []);

  useEffect(() => {
    // Check URL params
    const browserParam = searchParams.get('showBrowser');
    const storageKey = searchParams.get('storageKey');
    const dataParam = searchParams.get('data');
    let parsed = null;
    
    if (browserParam !== null) setShowBrowser(browserParam !== 'false');
    
    // Priority 1: Try data from URL (for archive screenshots)
    if (dataParam) {
      try {
        parsed = JSON.parse(decodeURIComponent(dataParam));
      } catch (e) {
        console.error("Error parsing URL data:", e);
      }
    }
    
    // Priority 2: Try storageKey
    if (!parsed && storageKey) {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          parsed = JSON.parse(stored);
        }
      } catch (e) {
        console.error("Error parsing storageKey data:", e);
      }
    }
    
    // Priority 3: Try standard localStorage key
    if (!parsed) {
      try {
        const stored = localStorage.getItem('bybit_preview_data');
        if (stored) {
          parsed = JSON.parse(stored);
        }
      } catch (e) {
        console.error("Error parsing localStorage data:", e);
      }
    }
    
    if (parsed) {
      setData(parsed);
      if (parsed.showBrowser !== undefined) setShowBrowser(parsed.showBrowser);
      // Use balance from OZON profile in RUB only
      const rubBalance = parsed.balance || 0;
      setBalances({
        RUB: rubBalance,
      });
    }
  }, [searchParams]);

  // Browser Frame Component
  const BrowserFrame = () => {
    const theme = {
      tabBarBg: '#202124',
      activeTabBg: '#35363a',
      inactiveTabBg: '#202124',
      addressBarBg: '#292a2d',
      textPrimary: '#e8eaed',
      textSecondary: '#9aa0a6',
    };

    return (
      <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
        <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
          <div className="flex items-end flex-1 pl-2">
            <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px]" style={{ backgroundColor: theme.inactiveTabBg }}>
              <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
            </div>
            <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[180px] max-w-[240px]" style={{ backgroundColor: theme.activeTabBg }}>
              <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>Трейдинг P2P</span>
              <span className="ml-2 text-[13px]" style={{ color: theme.textSecondary }}>×</span>
            </div>
          </div>
        </div>
        <div className="flex items-center h-[40px] px-4 gap-4" style={{ backgroundColor: theme.addressBarBg }}>
          <div className="flex gap-2">
            {['←', '→', '↻'].map((arrow, i) => (
              <button key={i} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ color: theme.textSecondary }}>
                {arrow}
              </button>
            ))}
          </div>
          <div className="flex-1 h-[32px] rounded-full px-4 flex items-center" style={{ backgroundColor: '#202124' }}>
            <span className="text-[14px]" style={{ color: theme.textSecondary }}>🔒 treider2p.com</span>
          </div>
        </div>
      </div>
    );
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="text-white text-lg">Загрузка...</div>
      </div>
    );
  }

  const menuItems = [
    { icon: Home, label: 'Главная', active: true },
    { icon: TrendingUp, label: 'P2P Торговля' },
    { icon: Wallet, label: 'Кошелёк' },
    { icon: History, label: 'История' },
    { icon: MessageSquare, label: 'Сообщения' },
    { icon: User, label: 'Профиль' },
    { icon: Shield, label: 'Безопасность' },
    { icon: Settings, label: 'Настройки' },
  ];

  const totalRUB = parseFloat(balances.RUB || 0);

  return (
    <div className="h-screen flex flex-col" style={{ backgroundColor: '#1a1a2e', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {showBrowser && <BrowserFrame />}
      <div className="flex flex-1 min-h-0" style={{ backgroundColor: '#1a1a2e' }}>
      {/* Sidebar */}
      <aside className="w-64 bg-[#1a1a2e] flex flex-col border-r border-gray-800 flex-shrink-0">
        {/* Logo */}
        <div className="p-5 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-xl">P2P Exchange</h1>
              <p className="text-gray-500 text-xs">Crypto Trading</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                className={`w-full flex items-center gap-3 px-5 py-3 text-sm transition-all ${
                  item.active 
                    ? 'bg-amber-500/10 text-amber-400 border-r-2 border-amber-400' 
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-800">
          <button className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg">
            <LogOut className="w-5 h-5" />
            <span>Выйти</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0" style={{ backgroundColor: '#1a1a2e' }}>
        {/* Header */}
        <header className="flex-shrink-0 bg-[#1a1a2e] border-b border-gray-800 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Добро пожаловать!</h2>
            <p className="text-gray-500 text-sm">P2P торговля криптовалютой</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Верифицирован</span>
            </div>
            
            <button className="p-2 text-gray-400 hover:text-white relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full"></span>
            </button>
            
            <div className="flex items-center gap-3 pl-4 border-l border-gray-700">
              <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">
                  {data.firstName?.[0]}{data.lastName?.[0]}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">{data.fullName}</p>
                <p className="text-xs text-gray-500">@{data.username}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto min-h-0 flex flex-col" style={{ backgroundColor: '#1a1a2e' }}>
          {/* Balance Card */}
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl p-6 text-white mb-6 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm mb-1">Общий баланс</p>
                <h3 className="text-4xl font-bold">{totalRUB.toLocaleString('ru-RU')} ₽</h3>
                <p className="text-white/70 text-sm mt-2 flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  {data.totalTrades} успешных сделок
                </p>
              </div>
              <div className="flex gap-3">
                <button className="px-5 py-2.5 bg-white/20 backdrop-blur hover:bg-white/30 rounded-xl font-medium flex items-center gap-2">
                  <ArrowUpCircle className="w-4 h-4" />
                  Купить
                </button>
                <button className="px-5 py-2.5 bg-white text-amber-600 hover:bg-gray-100 rounded-xl font-medium flex items-center gap-2">
                  <ArrowDownCircle className="w-4 h-4" />
                  Продать
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 flex-1" style={{ gridTemplateRows: '1fr' }}>
            {/* Assets */}
            <div className="col-span-2 bg-[#1a1a2e] rounded-2xl border border-gray-800 overflow-hidden">
              <div className="p-5 border-b border-gray-800 flex items-center justify-between">
                <h3 className="font-semibold text-white">Мои активы</h3>
                <button className="text-amber-400 text-sm font-medium hover:text-amber-300 flex items-center gap-1">
                  Все активы <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="divide-y divide-gray-800">
                {[
                  { symbol: 'RUB', name: 'Российский рубль', balance: totalRUB.toLocaleString('ru-RU') + ' ₽', color: '#8B5CF6' },
                ].map((asset) => (
                  <div key={asset.symbol} className="p-4 flex items-center justify-between hover:bg-white/5">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: asset.color }}>
                        ₽
                      </div>
                      <div>
                        <p className="font-medium text-white">{asset.symbol}</p>
                        <p className="text-gray-500 text-sm">{asset.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-white">{asset.balance}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats & Info */}
            <div className="space-y-4">
              {/* Trading Stats */}
              <div className="bg-[#1a1a2e] rounded-2xl border border-gray-800 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Статистика</p>
                    <p className="text-gray-500 text-sm">Ваша торговля</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Всего сделок</span>
                    <span className="text-white font-medium">{data.totalTrades}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Успешность</span>
                    <span className="text-green-400 font-medium">{data.successRate}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Рейтинг</span>
                    <span className="text-amber-400 font-medium flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" /> 4.9
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">ID трейдера</span>
                    <span className="text-white font-medium">{data.userId}</span>
                  </div>
                </div>
              </div>

              {/* Verification */}
              <div className="bg-[#1a1a2e] rounded-2xl border border-gray-800 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">Верификация</p>
                    <p className="text-green-400 text-sm">Полная</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Email подтверждён</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Телефон подтверждён</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span>Документы проверены</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}
